import { Datepicker, locale } from "@mobiscroll/react";
import { Box, IconButton, useTheme, useMediaQuery, Typography } from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { InlineTimeEdit } from "../schedule-event-time.component";

type Props = {
  editTime: InlineTimeEdit;
  setEditTime: React.Dispatch<React.SetStateAction<InlineTimeEdit>>;
  isVehicleStartEditable: boolean;
  confirmChangesOnClose: () => void;
  cancelChanges: () => void;
};

export function ScheduleEventTimeVehiclePartial({
  editTime,
  setEditTime,
  isVehicleStartEditable,
  confirmChangesOnClose,
  cancelChanges
}: Readonly<Props>) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { timeFormat, dateFormat } = useTimeFormat();
  const dateFormatFull = dateFormat + " " + timeFormat;

  return (
    <>
      <Box
        sx={{
          "& .mbsc-material.mbsc-textfield-wrapper": { m: 0 },
          maxWidth: 160
        }}
      >
        <Datepicker
          controls={["calendar", "time"]}
          stepMinute={15}
          disabled={!isVehicleStartEditable}
          value={editTime.value.start ? new Date(editTime.value.start) : null}
          dateFormatFull={dateFormatFull}
          locale={locale[i18n.language]}
          touchUi={isMobile}
          data-testid="vehicle-start-datetime-picker"
          onChange={picker => {
            if (picker.value) {
              const newStart = DateTime.fromJSDate(picker.value as Date).toISO() || "";
              setEditTime(e => ({
                ...e,
                value: { ...e.value, start: newStart }
              }));
            }
          }}
          min={DateTime.now().toJSDate()}
        />
      </Box>

      <Typography variant="body2">–</Typography>

      <Box
        sx={{
          "& .mbsc-material.mbsc-textfield-wrapper": { m: 0 },
          maxWidth: 160
        }}
      >
        <Datepicker
          controls={["calendar", "time"]}
          stepMinute={15}
          value={editTime.value.end ? new Date(editTime.value.end) : null}
          dateFormatFull={dateFormatFull}
          locale={locale[i18n.language]}
          touchUi={isMobile}
          data-testid="vehicle-end-datetime-picker"
          onChange={picker => {
            if (picker.value) {
              const newEnd = DateTime.fromJSDate(picker.value as Date).toISO() || "";
              setEditTime(e => ({
                ...e,
                value: { ...e.value, end: newEnd }
              }));
            }
          }}
          min={editTime.value.start ? new Date(editTime.value.start) : DateTime.now().toJSDate()}
        />
      </Box>

      <IconButton onClick={confirmChangesOnClose} data-testid="vehicle-confirm-edit" size="small">
        <Check fontSize="small" />
      </IconButton>
      <IconButton onClick={cancelChanges} data-testid="vehicle-cancel-edit" size="small">
        <Close fontSize="small" />
      </IconButton>
    </>
  );
}
